'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import Modal from 'react-modal';
import classNames from 'classnames';
import indexBy from 'lodash.indexby';
import uuid from 'uuid';
import moment from 'moment';

import SchedulingModal from '../../Recipes/SchedulingModal.react';
import EditImage from '../../Recipes/Editor/EditImage.react';
import FoodUnitsSelector from '../../DailyLog/FoodUnitsSelectorModal.react';
import EditMealServingsModal from '../../Recipes/EditMealServingsModal.react';
import NutritionInfo from '../../Products/NutritionInfo.react';
import FavoriteButton from '../../Widgets/FavoriteButton.react';
import RecipeInstructions from '../../Modules/RecipeInstructions.react';
import RecipeIngredients from '../../Modules/RecipeIngredients.react';
import ImgResized from '../../Widgets/ImgResized.react';
import Alert from '../../Widgets/Alert/Alert.react';
import SharePopup from '../../Widgets/SharePopup.react';
import LogPortionsModal from '../../Planner/Modals/LogPortionsModal.react';
import FoodEditorModal from '../../Foods/Modals/FoodEditorModal';
import Orders from '../../MealDetails/Orders.react';

import TaxonomyStore from '../../../stores/TaxonomyStore';
import TaxonomyActions from '../../../actions/TaxonomyActions';
import UserStore from '../../../stores/UserStore';
import MealActions from '../../../actions/MealActions';

import { inquire } from '../../../utils/Enforcer';
import { getNutrientsForMeals } from '../../../utils/Nutrition';
import { updateMealLeftovers, getDefaultParticipantsForRecipe } from '../../../utils/Meals';
import { fetchDocumentsById, getDefaultPortionsForContents } from '../../../utils/Content';
import { toHuman } from '../../../utils/Duration';
import { getConfig } from '../../../utils/Env';
import { ScreenService } from '../../../utils/ScreenService';

import allMealKitBrands from '../../../tables/meal-kit-brands';

import './FoodDetailsModal.scss';
import Analytics from '../../../utils/Analytics';
import MealTitle from '../../Meals/MealTitle.react';

export default class FoodDetailsModal extends Component {
    static propTypes = {
        modalTitle: PropTypes.string,
        addInstructionsText: PropTypes.node,
        uuid: PropTypes.string,
        food: PropTypes.object,
        defaultScaling: PropTypes.number,
        defaultParticipants: PropTypes.array,
        onSelectFood: PropTypes.func,
        selectButtonText: PropTypes.string,
        hideScheduleButton: PropTypes.bool,
        editableParticipants: PropTypes.bool,
        sourceMetaData: PropTypes.object
    };

    static defaultProps = {
        modalTitle: 'Food',
        selectButtonText: 'Add Food',
    };

    static contextTypes = {
        router: PropTypes.object,
        location: PropTypes.object,
        profile: PropTypes.object,
        isMobile: PropTypes.bool,
        showLoginForm: PropTypes.func,
    };

    constructor(props, context) {
        super(props, context);

        const { profile } = context;
        const user = UserStore.getUser();

        this.state = {
            loading: false,
            synced: false,
            user,
            profile: profile || user,

            nutrients: null,

            isNutritionToggled: context.defaultIsNutritionToggled ? true : false,
            isEditModalOpen: false,

            keyIndex: TaxonomyStore.getKeyIndex(),
        };
    }

    componentDidMount = () => {
        const { uuid, food } = this.props;

        ScreenService.keepAwake();

        if (uuid) {
            this.loadFood(uuid);
        } else if (food?.uuid) {
            this.loadFood(food.uuid);
        }

        TaxonomyActions.ensureLoaded();
        TaxonomyStore.addChangeListener(this.onTaxonomyStoreChange);
        UserStore.addChangeListener(this.onUserStoreChange);
    };

    componentWillUnmount = () => {
        ScreenService.allowSleep();
        TaxonomyStore.removeChangeListener(this.onTaxonomyStoreChange);
        UserStore.removeChangeListener(this.onUserStoreChange);
    };

    onUserStoreChange = () => {
        this.setState({ user: UserStore.getUser() });
    };

    onTaxonomyStoreChange = () => {
        this.setState({
            keyIndex: TaxonomyStore.getKeyIndex(),
        });
    };

    loadFood = (uuid) => {
        this.setState({ loading: true });

        // First, inquire to see if we have write and publish permission.
        const inquiries = [
            { action: 'publish', resource: uuid },
            { action: 'write', resource: uuid },
        ];

        inquire(inquiries).then((decisions) => {
            if (!decisions) {
                this.setState({ canWrite: false });
                return;
            }

            let canPublish = decisions.filter((d) => d.action == 'publish')[0].decision === 'permit';
            let canWrite = decisions.filter((d) => d.action == 'write')[0].decision === 'permit';

            this.setState({ canPublish, canWrite }, this.syncAssets);
        });

        // Then load the food data itself
        return fetchDocumentsById([uuid]).then((documents) => {
            const food = documents[0];

            if (!food) {
                return this.setState({ loading: false });
            }

            const portions = getDefaultPortionsForContents([food], this.state.profile);

            this.setState(
                {
                    food,
                    portion: portions[food.uuid],
                },
                this.syncAssets
            );
        });
    };

    syncAssets = () => {
        let { food, portion, profile, participants } = this.state;

        if (!food) {
            return;
        }

        // First, what factor are we going to multiply by?
        const nutrients = getNutrientsForMeals([portion], indexBy([food], 'uuid'), profile?.portion || 1, null);

        if (!participants && food.servings) {
            const { defaultParticipants } = this.props;
            participants = defaultParticipants || getDefaultParticipantsForRecipe(food, profile);

            // const needed = participants.reduce((total, member) => total + (member.portion || 1), 0);
            // scaling = defaultScaling || Math.ceil(needed / food.servings);
        }

        const areNutrientsEntered = Object.keys(nutrients).length > 0;
        const hasEditButton = this.shouldShowEditButton();

        this.setState({
            synced: true,
            loading: false,
            nutrients,
            portion,
            areNutrientsEntered,
            hasEditButton,
            participants,
        });
    };

    shouldShowEditButton = () => {
        const { user, food, canWrite } = this.state;

        // If there is no food loaded or no user, don't show the edit button
        if (!food || !user) {
            return false;
        }

        // For live foods or foods not owned by the current user, we can still edit by creating a copy
        // Similar to how recipes work - any logged in user can edit (which creates a copy when needed)
        return true;
    };

    openEditModal = () => {
        this.setState({ isEditModalOpen: true });
    };

    closeEditModal = () => {
        this.setState({ isEditModalOpen: false });
    };

    showEditMealServings = () => {
        this.setState({ isEditingParticipants: true });
    };

    closeParticipantModal = () => {
        this.setState({ isEditingParticipants: false });
    };

    openScheduleModal = () => {
        this.setState({ isScheduling: true, date: null, mealType: null });
    };

    closeScheduleModal = () => {
        this.setState({ isScheduling: false, date: null, mealType: null });
    };

    closeLogPortionsModal = () => {
        this.setState({ isLogPortionsOpen: false });
    };

    onSelectDateMealType = (date, mealType) => {
        const { portion } = this.state;
        // @todo - we need to decide if we should proceed straight to adding to calendar, or if we need to
        // ask the user how much should we add.

        // If the default is 1 serving and we're adding to a future date, just add it directly.
        if (
            portion.logged_amount == 1 &&
            (portion.logged_unit == 'serving' || !portion.logged_unit) &&
            moment().isSameOrBefore(date, 'day')
        ) {
            this.addToCalendar(portion, date, mealType);
            return;
        }

        this.setState({ isLogPortionsOpen: true, date, mealType });
    };

    onCompleteSignIn = () => {
        const { date, mealType } = this.state;

        this.addToCalendar(date, mealType);
    };

    onCompleteSignUp = () => {
        const { date, mealType } = this.state;

        this.addToCalendar(date, mealType);
    };

    addToCalendar = (portion, date, mealType = 'Dinner') => {
        const { user, profile, food, participants } = this.state;
        const { location, router, showLoginForm, showUpgradeForm } = this.context;

        if (!food) {
            return false;
        }

        if (!user) {
            // Did the user come from an email or google?
            const loginFormOpts = {
                defaultMode:
                    location &&
                    location.query &&
                    (location.query.utm_source == 'rd_recommendation' || location.query.showLogin == '1')
                        ? 'sign-in'
                        : 'sign-up',
                signupLinkSwitchesModes: true,
                onCompleteSignUp: this.onCompleteSignUp,
                onCompleteSignIn: this.onCompleteSignIn,
                signUpProfile: {},
                offModalCta: 'You are one more step away from finding delicious meals just for you.',
            };

            // Store our date for later
            this.setState({ portion, date, mealType });
            showLoginForm && showLoginForm(loginFormOpts);
            return;
        }

        const { capabilities = {} } = user;

        if (food.protection !== 'public' && !capabilities.meal_planner) {
            showUpgradeForm({ feature: 'meal_planner' });

            return false;
        }

        let dateFormatted = date.format('YYYY-MM-DD');

        let needed = participants ? participants.reduce((total, member) => total + member.portion, 0) : 1;
        let scaling = food.servings > 0 ? Math.ceil(needed / (food.servings || 1)) : null;

        let meal = {
            uuid: uuid.v4(),
            meal: mealType,
            meal_type: 'food',
            food_uuid: food.uuid,
            food_title: food.title,
            food_image: food.image,
            date: dateFormatted,
            created: moment().format(),
            participants: participants ? participants.map((p) => p.uuid || '').join(',') : null,
            scaling,
            ...portion,
        };

        let { dirtyMeals } = updateMealLeftovers(meal, [], food, profile, [meal]);

        MealActions.upsertMeals(dirtyMeals);

        router.push({ pathname: `/meals/${dateFormatted}/${mealType}` });

        return true;
    };

    renderCallToAction = () => {
        const { onSelectFood, hideScheduleButton, sourceMetaData } = this.props;
        const { user, portion, food, participants } = this.state;
        const { addSwapContext } = this.context;
        const { capabilities = {} } = user || {};
        const isLogging = addSwapContext && addSwapContext.auto_log ? true : false;
        const { logged_unit, logged_amount, logged_grams, logged_milliliters } = portion;

        if (!food) {
            return;
        }

        if (onSelectFood) {
            return (
                <button
                    className="el-toolbar-green-pill-btn select-btn"
                    onClick={() =>
                        onSelectFood(food, logged_unit, logged_amount, logged_grams, logged_milliliters, participants, sourceMetaData)
                    }
                >
                    {isLogging ? 'log meal' : 'select food'}
                    {food?.protection !== 'public' && !capabilities.meal_planner ? <i className="icon-lock" /> : null}
                </button>
            );
        }

        if (hideScheduleButton) {
            return;
        }

        return (
            <button className="el-toolbar-green-pill-btn" onClick={this.openScheduleModal}>
                schedule it
                {food.protection !== 'public' && !capabilities.meal_planner ? <i className="icon-lock" /> : null}
            </button>
        );
    };

    renderControlBar = () => {
        const { isMobile } = this.context;
        const { food, hasEditButton, canPublish } = this.state;

        if (!food) {
            return;
        }

        return (
            <div className={classNames('control-bar-container el-toolbar1', !isMobile ? 'el-toolbar1-dark' : null)}>
                {canPublish ? (
                    <a
                        target="_blank"
                        rel="noreferrer"
                        href={'https://' + getConfig('www_host') + `/admin/foods/${food.uuid}`}
                    >
                        <button className="admin-btn el-toolbar-btn">
                            <i className="feather feather-box" />
                            <em>admin</em>
                        </button>
                    </a>
                ) : null}

                <SharePopup className="el-toolbar-popup" food={food} />

                {hasEditButton ? (
                    <button className="edit-meal-btn el-toolbar-btn" onClick={this.openEditModal}>
                        <i className="feather feather-edit" />
                        <em>edit</em>
                    </button>
                ) : null}

                {this.renderCallToAction()}
            </div>
        );
    };

    renderEditMealServingsButton = () => {
        const { food, participants } = this.state;

        if (!participants) {
            return;
        }

        const totalParticipants = participants?.length || 1;

        return (
            <div className="edit-meal-servings" onClick={this.showEditMealServings}>
                <section className="serves">
                    <span>Serves</span>{' '}
                    <span>
                        {totalParticipants} {totalParticipants === 1 ? 'person' : 'people'}
                    </span>
                </section>

                <section className="timings">
                    {food.total_time ? (
                        <p>
                            {toHuman(food.total_time, true)}
                            <br />
                            prep/cook
                        </p>
                    ) : null}
                </section>
            </div>
        );
    };

    renderEditMealServingsModal = () => {
        const { food, profile, participants, scaling, isEditingParticipants } = this.state;
        const { editableParticipants } = this.props;

        if (!food || !isEditingParticipants) {
            return;
        }

        return (
            <EditMealServingsModal
                closeModal={this.closeParticipantModal}
                content={food}
                scaling={scaling}
                profile={profile}
                participants={participants}
                editableParticipants={editableParticipants}
                onChangeParticipants={this.onChangeParticipants}
            />
        );
    };

    onSaveFood = (food) => {
        const portions = getDefaultPortionsForContents([food], this.state.profile);

        this.setState({ food, portion: portions[food.uuid] }, this.syncAssets);

        Analytics.savedCustomFood({
            Context: 'Food Details',
            'Food UUID': food.uuid,
        });

        this.closeEditModal();
    };

    renderFoodEditorModal = () => {
        const { food, profile, isEditModalOpen } = this.state;

        if (!isEditModalOpen) {
            return;
        }

        return (
            <FoodEditorModal
                modalTitle="Edit Custom Food"
                contentLabel="Edit Recipe Details"
                food={food}
                profile={profile}
                closeModal={this.closeEditModal}
                onSaveFood={this.onSaveFood}
            />
        );
    };

    renderSchedulingModal = () => {
        const { food, isScheduling } = this.state;

        if (!isScheduling) {
            return null;
        }

        return (
            <SchedulingModal
                content={food}
                selectDate={this.onSelectDateMealType}
                closeModal={this.closeScheduleModal}
            />
        );
    };

    onSelectPortions = (contents, portions) => {
        const { food, date, mealType } = this.state;

        const portion = portions[food.uuid];

        this.addToCalendar(portion, date, mealType);
    };

    renderLogPortionsModal = () => {
        const { profile, food, portion, isLogPortionsOpen } = this.state;

        if (!isLogPortionsOpen) {
            return;
        }

        return (
            <LogPortionsModal
                closeModal={this.closeLogPortionsModal}
                requirePrecise={false}
                profile={profile}
                modalTitle="How much?"
                ctaText="Add Food"
                contents={[food]}
                defaultPortions={[portion]}
                onSelectPortions={this.onSelectPortions}
            />
        );
    };

    onChangePortionAmount = (logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters, unit) => {
        const { portion } = this.state;

        Object.assign(portion, { logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters, unit });

        this.setState({ portion }, this.syncAssets);
    };

    renderPicsOrItDidntHappen = () => {
        const { food } = this.state;

        if (!food.image && !food.nfp_image && !food.ingredients_image) {
            return;
        }

        return (
            <div className="image-editor">
                <div className="image-inputs">
                    {food.image ? (
                        <div className="image-input">
                            <EditImage image={food.image} showFooter={false} />
                        </div>
                    ) : null}

                    {food.nfp_image ? (
                        <div className="image-input">
                            <EditImage image={food.nfp_image} showFooter={false} />
                        </div>
                    ) : null}

                    {food.ingredients_image ? (
                        <div className="image-input">
                            <EditImage image={food.ingredients_image} showFooter={false} />
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    renderModalBody = () => {
        const { food, portion, loading, synced, areNutrientsEntered, hasEditButton, assets = {} } = this.state;
        const { profile, nutrients, scaling = 1 } = this.state;
        const { isMobile } = this.context;
        const { hide_nutrition = false, rd_override = false } = (profile && profile.preferences) || {};

        if (loading || !synced) {
            return (
                <div className="details-loading el-fonts">
                    <h4>Loading food</h4>
                    <i className="icon-spinner2" />
                </div>
            );
        }

        const sections = ['first', 'second', 'third', 'fourth'];
        let section = 0;

        const typeMap = {
            'Restaurant Dish': 'Restaurant Dish',
            'Ready Made Meal': 'Ready Made Meal',
            'Brand Name Food': 'Store Bought',
            'Meal Kit': 'Meal Kit',
        };

        const dishTitles = {
            'Restaurant Dish': (
                <>
                    <span>Order from</span> <em>{food.brand_name}</em>
                </>
            ),
            'Ready Made Meal': food.brand_name ? (
                <>
                    <span>Ready Made Meal by</span> <em>{food.brand_name}</em>
                </>
            ) : (
                'Ready Made Meal'
            ),
            'Brand Name Food': <span>Store Bought Item</span>,
            Food: <></>,
            UGC: <span>Custom Food</span>,
            'Meal Kit': food.brand_name ? (
                <>
                    <span>Meal kit by</span> <em>{food.brand_name}</em>
                </>
            ) : (
                'Meal kit'
            ),
        };

        let dishTitle = dishTitles[food.product_type] || food.product_type;

        let foodPrice = null;

        if (food.mapped_from?.length && food.servings) {
            const mf = food.mapped_from.find((mf) => mf.price);

            if (mf) {
                foodPrice = mf.price / food.servings;
            }
        }

        return (
            <>
                {!isMobile ? (
                    <div className="details-header">
                        {this.renderEditMealServingsButton()}

                        <header>{this.renderControlBar()}</header>
                    </div>
                ) : null}

                <div className="meals-scroll-hero" data-index={0} data-singleton={false}>
                    <div className="hero-item" style={{ transform: 'translateX(30px)' }}>
                        <div className="hero-item-container">
                            {food.product_type === 'Meal Kit' ? (
                                <svg
                                    className="hero-tag"
                                    width="71"
                                    height="39"
                                    viewBox="0 0 71 39"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M60.4624 0.5V39C60.4624 39 70.4624 34.9474 70.4624 31.5702V29.8228V10.5C70.4624 4.97715 65.9852 0.5 60.4624 0.5Z"
                                        fill="#AD8124"
                                    />
                                    <path
                                        d="M7.82607 16.7647L1.18925 30.5666C0.870011 31.2305 1.35381 32 2.09047 32L69.5 32C70.0523 32 70.5 31.5577 70.5 31.0054V0.982791C70.5 0.430506 70.0523 0 69.5 0H2.0619C1.33236 0 0.848365 0.755956 1.15369 1.41852L7.83306 15.9128C7.95785 16.1836 7.95529 16.496 7.82607 16.7647Z"
                                        fill="#DCB76B"
                                    />
                                    <path
                                        d="M27.581 12.324V21H26.159V15.396C26.159 15.172 26.171 14.93 26.195 14.67L23.573 19.596C23.449 19.832 23.259 19.95 23.003 19.95H22.775C22.519 19.95 22.329 19.832 22.205 19.596L19.553 14.652C19.565 14.784 19.575 14.914 19.583 15.042C19.591 15.17 19.595 15.288 19.595 15.396V21H18.173V12.324H19.391C19.463 12.324 19.525 12.326 19.577 12.33C19.629 12.334 19.675 12.344 19.715 12.36C19.759 12.376 19.797 12.402 19.829 12.438C19.865 12.474 19.899 12.522 19.931 12.582L22.529 17.4C22.597 17.528 22.659 17.66 22.715 17.796C22.775 17.932 22.833 18.072 22.889 18.216C22.945 18.068 23.003 17.926 23.063 17.79C23.123 17.65 23.187 17.516 23.255 17.388L25.817 12.582C25.849 12.522 25.883 12.474 25.919 12.438C25.955 12.402 25.993 12.376 26.033 12.36C26.077 12.344 26.125 12.334 26.177 12.33C26.229 12.326 26.291 12.324 26.363 12.324H27.581ZM33.0613 17.214C33.0613 17.022 33.0333 16.842 32.9773 16.674C32.9253 16.502 32.8453 16.352 32.7373 16.224C32.6293 16.096 32.4913 15.996 32.3233 15.924C32.1593 15.848 31.9673 15.81 31.7473 15.81C31.3193 15.81 30.9813 15.932 30.7333 16.176C30.4893 16.42 30.3333 16.766 30.2653 17.214H33.0613ZM30.2353 18.108C30.2593 18.424 30.3153 18.698 30.4033 18.93C30.4913 19.158 30.6073 19.348 30.7513 19.5C30.8953 19.648 31.0653 19.76 31.2613 19.836C31.4613 19.908 31.6813 19.944 31.9213 19.944C32.1613 19.944 32.3673 19.916 32.5393 19.86C32.7153 19.804 32.8673 19.742 32.9953 19.674C33.1273 19.606 33.2413 19.544 33.3373 19.488C33.4373 19.432 33.5333 19.404 33.6253 19.404C33.7493 19.404 33.8413 19.45 33.9013 19.542L34.3273 20.082C34.1633 20.274 33.9793 20.436 33.7753 20.568C33.5713 20.696 33.3573 20.8 33.1333 20.88C32.9133 20.956 32.6873 21.01 32.4553 21.042C32.2273 21.074 32.0053 21.09 31.7893 21.09C31.3613 21.09 30.9633 21.02 30.5953 20.88C30.2273 20.736 29.9073 20.526 29.6353 20.25C29.3633 19.97 29.1493 19.626 28.9933 19.218C28.8373 18.806 28.7593 18.33 28.7593 17.79C28.7593 17.37 28.8273 16.976 28.9633 16.608C29.0993 16.236 29.2933 15.914 29.5453 15.642C29.8013 15.366 30.1113 15.148 30.4753 14.988C30.8433 14.828 31.2573 14.748 31.7173 14.748C32.1053 14.748 32.4633 14.81 32.7913 14.934C33.1193 15.058 33.4013 15.24 33.6373 15.48C33.8733 15.716 34.0573 16.008 34.1893 16.356C34.3253 16.7 34.3933 17.094 34.3933 17.538C34.3933 17.762 34.3693 17.914 34.3213 17.994C34.2733 18.07 34.1813 18.108 34.0453 18.108H30.2353ZM38.7574 18.372C38.3294 18.392 37.9694 18.43 37.6774 18.486C37.3854 18.538 37.1514 18.606 36.9754 18.69C36.7994 18.774 36.6734 18.872 36.5974 18.984C36.5214 19.096 36.4834 19.218 36.4834 19.35C36.4834 19.61 36.5594 19.796 36.7114 19.908C36.8674 20.02 37.0694 20.076 37.3174 20.076C37.6214 20.076 37.8834 20.022 38.1034 19.914C38.3274 19.802 38.5454 19.634 38.7574 19.41V18.372ZM35.3614 15.702C36.0694 15.054 36.9214 14.73 37.9174 14.73C38.2774 14.73 38.5994 14.79 38.8834 14.91C39.1674 15.026 39.4074 15.19 39.6034 15.402C39.7994 15.61 39.9474 15.86 40.0474 16.152C40.1514 16.444 40.2034 16.764 40.2034 17.112V21H39.5314C39.3914 21 39.2834 20.98 39.2074 20.94C39.1314 20.896 39.0714 20.81 39.0274 20.682L38.8954 20.238C38.7394 20.378 38.5874 20.502 38.4394 20.61C38.2914 20.714 38.1374 20.802 37.9774 20.874C37.8174 20.946 37.6454 21 37.4614 21.036C37.2814 21.076 37.0814 21.096 36.8614 21.096C36.6014 21.096 36.3614 21.062 36.1414 20.994C35.9214 20.922 35.7314 20.816 35.5714 20.676C35.4114 20.536 35.2874 20.362 35.1994 20.154C35.1114 19.946 35.0674 19.704 35.0674 19.428C35.0674 19.272 35.0934 19.118 35.1454 18.966C35.1974 18.81 35.2814 18.662 35.3974 18.522C35.5174 18.382 35.6714 18.25 35.8594 18.126C36.0474 18.002 36.2774 17.894 36.5494 17.802C36.8254 17.71 37.1454 17.636 37.5094 17.58C37.8734 17.52 38.2894 17.484 38.7574 17.472V17.112C38.7574 16.7 38.6694 16.396 38.4934 16.2C38.3174 16 38.0634 15.9 37.7314 15.9C37.4914 15.9 37.2914 15.928 37.1314 15.984C36.9754 16.04 36.8374 16.104 36.7174 16.176C36.5974 16.244 36.4874 16.306 36.3874 16.362C36.2914 16.418 36.1834 16.446 36.0634 16.446C35.9594 16.446 35.8714 16.42 35.7994 16.368C35.7274 16.312 35.6694 16.248 35.6254 16.176L35.3614 15.702ZM43.0992 12.084V21H41.6172V12.084H43.0992ZM49.3417 15.96H49.7197C49.8717 15.96 49.9977 15.94 50.0977 15.9C50.1977 15.856 50.2837 15.788 50.3557 15.696L52.7497 12.666C52.8497 12.538 52.9537 12.45 53.0617 12.402C53.1737 12.35 53.3137 12.324 53.4817 12.324H54.8737L51.9517 15.93C51.7797 16.15 51.6017 16.306 51.4177 16.398C51.5497 16.446 51.6677 16.514 51.7717 16.602C51.8797 16.686 51.9817 16.798 52.0777 16.938L55.0897 21H53.6677C53.4757 21 53.3317 20.974 53.2357 20.922C53.1437 20.866 53.0657 20.786 53.0017 20.682L50.5477 17.478C50.4717 17.37 50.3837 17.294 50.2837 17.25C50.1837 17.206 50.0397 17.184 49.8517 17.184H49.3417V21H47.7277V12.324H49.3417V15.96ZM57.2563 14.844V21H55.7683V14.844H57.2563ZM57.4603 13.05C57.4603 13.178 57.4343 13.298 57.3823 13.41C57.3303 13.522 57.2603 13.62 57.1723 13.704C57.0883 13.788 56.9883 13.856 56.8723 13.908C56.7563 13.956 56.6323 13.98 56.5003 13.98C56.3723 13.98 56.2503 13.956 56.1343 13.908C56.0223 13.856 55.9243 13.788 55.8403 13.704C55.7563 13.62 55.6883 13.522 55.6363 13.41C55.5883 13.298 55.5643 13.178 55.5643 13.05C55.5643 12.918 55.5883 12.794 55.6363 12.678C55.6883 12.562 55.7563 12.462 55.8403 12.378C55.9243 12.294 56.0223 12.228 56.1343 12.18C56.2503 12.128 56.3723 12.102 56.5003 12.102C56.6323 12.102 56.7563 12.128 56.8723 12.18C56.9883 12.228 57.0883 12.294 57.1723 12.378C57.2603 12.462 57.3303 12.562 57.3823 12.678C57.4343 12.794 57.4603 12.918 57.4603 13.05ZM60.8192 21.096C60.2832 21.096 59.8712 20.946 59.5832 20.646C59.2992 20.342 59.1572 19.924 59.1572 19.392V15.954H58.5272C58.4472 15.954 58.3792 15.928 58.3232 15.876C58.2672 15.824 58.2392 15.746 58.2392 15.642V15.054L59.2292 14.892L59.5412 13.212C59.5612 13.132 59.5992 13.07 59.6552 13.026C59.7112 12.982 59.7832 12.96 59.8712 12.96H60.6392V14.898H62.2832V15.954H60.6392V19.29C60.6392 19.482 60.6852 19.632 60.7772 19.74C60.8732 19.848 61.0032 19.902 61.1672 19.902C61.2592 19.902 61.3352 19.892 61.3952 19.872C61.4592 19.848 61.5132 19.824 61.5572 19.8C61.6052 19.776 61.6472 19.754 61.6832 19.734C61.7192 19.71 61.7552 19.698 61.7912 19.698C61.8352 19.698 61.8712 19.71 61.8992 19.734C61.9272 19.754 61.9572 19.786 61.9892 19.83L62.4332 20.55C62.2172 20.73 61.9692 20.866 61.6892 20.958C61.4092 21.05 61.1192 21.096 60.8192 21.096Z"
                                        fill="white"
                                    />
                                </svg>
                            ) : null}

                            {food.product_type === 'Ready Made Meal' ? (
                                <svg
                                    className="hero-tag"
                                    width="91"
                                    height="39"
                                    viewBox="0 0 91 39"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M48.081 8.324V17H46.659V11.396C46.659 11.172 46.671 10.93 46.695 10.67L44.073 15.596C43.949 15.832 43.759 15.95 43.503 15.95H43.275C43.019 15.95 42.829 15.832 42.705 15.596L40.053 10.652C40.065 10.784 40.075 10.914 40.083 11.042C40.091 11.17 40.095 11.288 40.095 11.396V17H38.673V8.324H39.891C39.963 8.324 40.025 8.326 40.077 8.33C40.129 8.334 40.175 8.344 40.215 8.36C40.259 8.376 40.297 8.402 40.329 8.438C40.365 8.474 40.399 8.522 40.431 8.582L43.029 13.4C43.097 13.528 43.159 13.66 43.215 13.796C43.275 13.932 43.333 14.072 43.389 14.216C43.445 14.068 43.503 13.926 43.563 13.79C43.623 13.65 43.687 13.516 43.755 13.388L46.317 8.582C46.349 8.522 46.383 8.474 46.419 8.438C46.455 8.402 46.493 8.376 46.533 8.36C46.577 8.344 46.625 8.334 46.677 8.33C46.729 8.326 46.791 8.324 46.863 8.324H48.081ZM53.5613 13.214C53.5613 13.022 53.5333 12.842 53.4773 12.674C53.4253 12.502 53.3453 12.352 53.2373 12.224C53.1293 12.096 52.9913 11.996 52.8233 11.924C52.6593 11.848 52.4673 11.81 52.2473 11.81C51.8193 11.81 51.4813 11.932 51.2333 12.176C50.9893 12.42 50.8333 12.766 50.7653 13.214H53.5613ZM50.7353 14.108C50.7593 14.424 50.8153 14.698 50.9033 14.93C50.9913 15.158 51.1073 15.348 51.2513 15.5C51.3953 15.648 51.5653 15.76 51.7613 15.836C51.9613 15.908 52.1813 15.944 52.4213 15.944C52.6613 15.944 52.8673 15.916 53.0393 15.86C53.2153 15.804 53.3673 15.742 53.4953 15.674C53.6273 15.606 53.7413 15.544 53.8373 15.488C53.9373 15.432 54.0333 15.404 54.1253 15.404C54.2493 15.404 54.3413 15.45 54.4013 15.542L54.8273 16.082C54.6633 16.274 54.4793 16.436 54.2753 16.568C54.0713 16.696 53.8573 16.8 53.6333 16.88C53.4133 16.956 53.1873 17.01 52.9553 17.042C52.7273 17.074 52.5053 17.09 52.2893 17.09C51.8613 17.09 51.4633 17.02 51.0953 16.88C50.7273 16.736 50.4073 16.526 50.1353 16.25C49.8633 15.97 49.6493 15.626 49.4933 15.218C49.3373 14.806 49.2593 14.33 49.2593 13.79C49.2593 13.37 49.3273 12.976 49.4633 12.608C49.5993 12.236 49.7933 11.914 50.0453 11.642C50.3013 11.366 50.6113 11.148 50.9753 10.988C51.3433 10.828 51.7573 10.748 52.2173 10.748C52.6053 10.748 52.9633 10.81 53.2913 10.934C53.6193 11.058 53.9013 11.24 54.1373 11.48C54.3733 11.716 54.5573 12.008 54.6893 12.356C54.8253 12.7 54.8933 13.094 54.8933 13.538C54.8933 13.762 54.8693 13.914 54.8213 13.994C54.7733 14.07 54.6813 14.108 54.5453 14.108H50.7353ZM59.2574 14.372C58.8294 14.392 58.4694 14.43 58.1774 14.486C57.8854 14.538 57.6514 14.606 57.4754 14.69C57.2994 14.774 57.1734 14.872 57.0974 14.984C57.0214 15.096 56.9834 15.218 56.9834 15.35C56.9834 15.61 57.0594 15.796 57.2114 15.908C57.3674 16.02 57.5694 16.076 57.8174 16.076C58.1214 16.076 58.3834 16.022 58.6034 15.914C58.8274 15.802 59.0454 15.634 59.2574 15.41V14.372ZM55.8614 11.702C56.5694 11.054 57.4214 10.73 58.4174 10.73C58.7774 10.73 59.0994 10.79 59.3834 10.91C59.6674 11.026 59.9074 11.19 60.1034 11.402C60.2994 11.61 60.4474 11.86 60.5474 12.152C60.6514 12.444 60.7034 12.764 60.7034 13.112V17H60.0314C59.8914 17 59.7834 16.98 59.7074 16.94C59.6314 16.896 59.5714 16.81 59.5274 16.682L59.3954 16.238C59.2394 16.378 59.0874 16.502 58.9394 16.61C58.7914 16.714 58.6374 16.802 58.4774 16.874C58.3174 16.946 58.1454 17 57.9614 17.036C57.7814 17.076 57.5814 17.096 57.3614 17.096C57.1014 17.096 56.8614 17.062 56.6414 16.994C56.4214 16.922 56.2314 16.816 56.0714 16.676C55.9114 16.536 55.7874 16.362 55.6994 16.154C55.6114 15.946 55.5674 15.704 55.5674 15.428C55.5674 15.272 55.5934 15.118 55.6454 14.966C55.6974 14.81 55.7814 14.662 55.8974 14.522C56.0174 14.382 56.1714 14.25 56.3594 14.126C56.5474 14.002 56.7774 13.894 57.0494 13.802C57.3254 13.71 57.6454 13.636 58.0094 13.58C58.3734 13.52 58.7894 13.484 59.2574 13.472V13.112C59.2574 12.7 59.1694 12.396 58.9934 12.2C58.8174 12 58.5634 11.9 58.2314 11.9C57.9914 11.9 57.7914 11.928 57.6314 11.984C57.4754 12.04 57.3374 12.104 57.2174 12.176C57.0974 12.244 56.9874 12.306 56.8874 12.362C56.7914 12.418 56.6834 12.446 56.5634 12.446C56.4594 12.446 56.3714 12.42 56.2994 12.368C56.2274 12.312 56.1694 12.248 56.1254 12.176L55.8614 11.702ZM63.5992 8.084V17H62.1172V8.084H63.5992ZM69.8417 11.96H70.2197C70.3717 11.96 70.4977 11.94 70.5977 11.9C70.6977 11.856 70.7837 11.788 70.8557 11.696L73.2497 8.666C73.3497 8.538 73.4537 8.45 73.5617 8.402C73.6737 8.35 73.8137 8.324 73.9817 8.324H75.3737L72.4517 11.93C72.2797 12.15 72.1017 12.306 71.9177 12.398C72.0497 12.446 72.1677 12.514 72.2717 12.602C72.3797 12.686 72.4817 12.798 72.5777 12.938L75.5897 17H74.1677C73.9757 17 73.8317 16.974 73.7357 16.922C73.6437 16.866 73.5657 16.786 73.5017 16.682L71.0477 13.478C70.9717 13.37 70.8837 13.294 70.7837 13.25C70.6837 13.206 70.5397 13.184 70.3517 13.184H69.8417V17H68.2277V8.324H69.8417V11.96ZM77.7563 10.844V17H76.2683V10.844H77.7563ZM77.9603 9.05C77.9603 9.178 77.9343 9.298 77.8823 9.41C77.8303 9.522 77.7603 9.62 77.6723 9.704C77.5883 9.788 77.4883 9.856 77.3723 9.908C77.2563 9.956 77.1323 9.98 77.0003 9.98C76.8723 9.98 76.7503 9.956 76.6343 9.908C76.5223 9.856 76.4243 9.788 76.3403 9.704C76.2563 9.62 76.1883 9.522 76.1363 9.41C76.0883 9.298 76.0643 9.178 76.0643 9.05C76.0643 8.918 76.0883 8.794 76.1363 8.678C76.1883 8.562 76.2563 8.462 76.3403 8.378C76.4243 8.294 76.5223 8.228 76.6343 8.18C76.7503 8.128 76.8723 8.102 77.0003 8.102C77.1323 8.102 77.2563 8.128 77.3723 8.18C77.4883 8.228 77.5883 8.294 77.6723 8.378C77.7603 8.462 77.8303 8.562 77.8823 8.678C77.9343 8.794 77.9603 8.918 77.9603 9.05ZM81.3192 17.096C80.7832 17.096 80.3712 16.946 80.0832 16.646C79.7992 16.342 79.6572 15.924 79.6572 15.392V11.954H79.0272C78.9472 11.954 78.8792 11.928 78.8232 11.876C78.7672 11.824 78.7392 11.746 78.7392 11.642V11.054L79.7292 10.892L80.0412 9.212C80.0612 9.132 80.0992 9.07 80.1552 9.026C80.2112 8.982 80.2832 8.96 80.3712 8.96H81.1392V10.898H82.7832V11.954H81.1392V15.29C81.1392 15.482 81.1852 15.632 81.2772 15.74C81.3732 15.848 81.5032 15.902 81.6672 15.902C81.7592 15.902 81.8352 15.892 81.8952 15.872C81.9592 15.848 82.0132 15.824 82.0572 15.8C82.1052 15.776 82.1472 15.754 82.1832 15.734C82.2192 15.71 82.2552 15.698 82.2912 15.698C82.3352 15.698 82.3712 15.71 82.3992 15.734C82.4272 15.754 82.4572 15.786 82.4892 15.83L82.9332 16.55C82.7172 16.73 82.4692 16.866 82.1892 16.958C81.9092 17.05 81.6192 17.096 81.3192 17.096Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M81 3V39C81 39 91 35.2105 91 32.0526V30.4187V13C91 7.47715 86.5228 3 81 3Z"
                                        fill="#1E293F"
                                    />
                                    <path
                                        d="M7.40562 16.7685L0.698804 30.5627C0.375869 31.2269 0.859598 32 1.59814 32L90 32C90.5523 32 91 31.5577 91 31.0054V0.982791C91 0.430506 90.5523 0 90 0H1.56917C0.837809 0 0.353849 0.759419 0.66274 1.42235L7.41272 15.9089C7.53991 16.1819 7.53731 16.4977 7.40562 16.7685Z"
                                        fill="#2E3B55"
                                    />
                                    <path
                                        d="M20.5177 16.452C20.8217 16.452 21.0857 16.414 21.3097 16.338C21.5377 16.262 21.7237 16.158 21.8677 16.026C22.0157 15.89 22.1257 15.73 22.1977 15.546C22.2697 15.362 22.3057 15.16 22.3057 14.94C22.3057 14.5 22.1597 14.162 21.8677 13.926C21.5797 13.69 21.1377 13.572 20.5417 13.572H19.5097V16.452H20.5177ZM24.6697 21H23.2117C22.9357 21 22.7357 20.892 22.6117 20.676L20.7877 17.898C20.7197 17.794 20.6437 17.72 20.5597 17.676C20.4797 17.632 20.3597 17.61 20.1997 17.61H19.5097V21H17.8957V12.324H20.5417C21.1297 12.324 21.6337 12.386 22.0537 12.51C22.4777 12.63 22.8237 12.8 23.0917 13.02C23.3637 13.24 23.5637 13.504 23.6917 13.812C23.8197 14.116 23.8837 14.452 23.8837 14.82C23.8837 15.112 23.8397 15.388 23.7517 15.648C23.6677 15.908 23.5437 16.144 23.3797 16.356C23.2197 16.568 23.0197 16.754 22.7797 16.914C22.5437 17.074 22.2737 17.2 21.9697 17.292C22.0737 17.352 22.1697 17.424 22.2577 17.508C22.3457 17.588 22.4257 17.684 22.4977 17.796L24.6697 21ZM28.9402 17.214C28.9402 17.022 28.9122 16.842 28.8562 16.674C28.8042 16.502 28.7242 16.352 28.6162 16.224C28.5082 16.096 28.3702 15.996 28.2022 15.924C28.0382 15.848 27.8462 15.81 27.6262 15.81C27.1982 15.81 26.8602 15.932 26.6122 16.176C26.3682 16.42 26.2122 16.766 26.1442 17.214H28.9402ZM26.1142 18.108C26.1382 18.424 26.1942 18.698 26.2822 18.93C26.3702 19.158 26.4862 19.348 26.6302 19.5C26.7742 19.648 26.9442 19.76 27.1402 19.836C27.3402 19.908 27.5602 19.944 27.8002 19.944C28.0402 19.944 28.2462 19.916 28.4182 19.86C28.5942 19.804 28.7462 19.742 28.8742 19.674C29.0062 19.606 29.1202 19.544 29.2162 19.488C29.3162 19.432 29.4122 19.404 29.5042 19.404C29.6282 19.404 29.7202 19.45 29.7802 19.542L30.2062 20.082C30.0422 20.274 29.8582 20.436 29.6542 20.568C29.4502 20.696 29.2362 20.8 29.0122 20.88C28.7922 20.956 28.5662 21.01 28.3342 21.042C28.1062 21.074 27.8842 21.09 27.6682 21.09C27.2402 21.09 26.8422 21.02 26.4742 20.88C26.1062 20.736 25.7862 20.526 25.5142 20.25C25.2422 19.97 25.0282 19.626 24.8722 19.218C24.7162 18.806 24.6382 18.33 24.6382 17.79C24.6382 17.37 24.7062 16.976 24.8422 16.608C24.9782 16.236 25.1722 15.914 25.4242 15.642C25.6802 15.366 25.9902 15.148 26.3542 14.988C26.7222 14.828 27.1362 14.748 27.5962 14.748C27.9842 14.748 28.3422 14.81 28.6702 14.934C28.9982 15.058 29.2802 15.24 29.5162 15.48C29.7522 15.716 29.9362 16.008 30.0682 16.356C30.2042 16.7 30.2722 17.094 30.2722 17.538C30.2722 17.762 30.2482 17.914 30.2002 17.994C30.1522 18.07 30.0602 18.108 29.9242 18.108H26.1142ZM34.6363 18.372C34.2083 18.392 33.8483 18.43 33.5563 18.486C33.2643 18.538 33.0303 18.606 32.8543 18.69C32.6783 18.774 32.5523 18.872 32.4763 18.984C32.4003 19.096 32.3623 19.218 32.3623 19.35C32.3623 19.61 32.4383 19.796 32.5903 19.908C32.7463 20.02 32.9483 20.076 33.1963 20.076C33.5003 20.076 33.7623 20.022 33.9823 19.914C34.2063 19.802 34.4243 19.634 34.6363 19.41V18.372ZM31.2403 15.702C31.9483 15.054 32.8003 14.73 33.7963 14.73C34.1563 14.73 34.4783 14.79 34.7623 14.91C35.0463 15.026 35.2863 15.19 35.4823 15.402C35.6783 15.61 35.8263 15.86 35.9263 16.152C36.0303 16.444 36.0823 16.764 36.0823 17.112V21H35.4103C35.2703 21 35.1623 20.98 35.0863 20.94C35.0103 20.896 34.9503 20.81 34.9063 20.682L34.7743 20.238C34.6183 20.378 34.4663 20.502 34.3183 20.61C34.1703 20.714 34.0163 20.802 33.8563 20.874C33.6963 20.946 33.5243 21 33.3403 21.036C33.1603 21.076 32.9603 21.096 32.7403 21.096C32.4803 21.096 32.2403 21.062 32.0203 20.994C31.8003 20.922 31.6103 20.816 31.4503 20.676C31.2903 20.536 31.1663 20.362 31.0783 20.154C30.9903 19.946 30.9463 19.704 30.9463 19.428C30.9463 19.272 30.9723 19.118 31.0243 18.966C31.0763 18.81 31.1603 18.662 31.2763 18.522C31.3963 18.382 31.5503 18.25 31.7383 18.126C31.9263 18.002 32.1563 17.894 32.4283 17.802C32.7043 17.71 33.0243 17.636 33.3883 17.58C33.7523 17.52 34.1683 17.484 34.6363 17.472V17.112C34.6363 16.7 34.5483 16.396 34.3723 16.2C34.1963 16 33.9423 15.9 33.6103 15.9C33.3703 15.9 33.1703 15.928 33.0103 15.984C32.8543 16.04 32.7163 16.104 32.5963 16.176C32.4763 16.244 32.3663 16.306 32.2663 16.362C32.1703 16.418 32.0623 16.446 31.9423 16.446C31.8383 16.446 31.7503 16.42 31.6783 16.368C31.6063 16.312 31.5483 16.248 31.5043 16.176L31.2403 15.702ZM41.3421 16.416C41.1741 16.212 40.9901 16.068 40.7901 15.984C40.5941 15.9 40.3821 15.858 40.1541 15.858C39.9301 15.858 39.7281 15.9 39.5481 15.984C39.3681 16.068 39.2141 16.196 39.0861 16.368C38.9581 16.536 38.8601 16.752 38.7921 17.016C38.7241 17.276 38.6901 17.584 38.6901 17.94C38.6901 18.3 38.7181 18.606 38.7741 18.858C38.8341 19.106 38.9181 19.31 39.0261 19.47C39.1341 19.626 39.2661 19.74 39.4221 19.812C39.5781 19.88 39.7521 19.914 39.9441 19.914C40.2521 19.914 40.5141 19.85 40.7301 19.722C40.9461 19.594 41.1501 19.412 41.3421 19.176V16.416ZM42.8241 12.084V21H41.9181C41.7221 21 41.5981 20.91 41.5461 20.73L41.4201 20.136C41.1721 20.42 40.8861 20.65 40.5621 20.826C40.2421 21.002 39.8681 21.09 39.4401 21.09C39.1041 21.09 38.7961 21.02 38.5161 20.88C38.2361 20.74 37.9941 20.538 37.7901 20.274C37.5901 20.006 37.4341 19.676 37.3221 19.284C37.2141 18.892 37.1601 18.444 37.1601 17.94C37.1601 17.484 37.2221 17.06 37.3461 16.668C37.4701 16.276 37.6481 15.936 37.8801 15.648C38.1121 15.36 38.3901 15.136 38.7141 14.976C39.0381 14.812 39.4021 14.73 39.8061 14.73C40.1501 14.73 40.4441 14.784 40.6881 14.892C40.9321 15 41.1501 15.146 41.3421 15.33V12.084H42.8241ZM49.8787 14.844L46.5247 22.692C46.4807 22.796 46.4227 22.874 46.3507 22.926C46.2827 22.982 46.1767 23.01 46.0327 23.01H44.9287L46.0807 20.538L43.5907 14.844H44.8927C45.0087 14.844 45.0987 14.872 45.1627 14.928C45.2307 14.984 45.2807 15.048 45.3127 15.12L46.6207 18.312C46.6647 18.42 46.7007 18.528 46.7287 18.636C46.7607 18.744 46.7907 18.854 46.8187 18.966C46.8547 18.854 46.8907 18.744 46.9267 18.636C46.9627 18.524 47.0027 18.414 47.0467 18.306L48.2827 15.12C48.3147 15.04 48.3667 14.974 48.4387 14.922C48.5147 14.87 48.5987 14.844 48.6907 14.844H49.8787ZM62.8701 12.324V21H61.4481V15.396C61.4481 15.172 61.4601 14.93 61.4841 14.67L58.8621 19.596C58.7381 19.832 58.5481 19.95 58.2921 19.95H58.0641C57.8081 19.95 57.6181 19.832 57.4941 19.596L54.8421 14.652C54.8541 14.784 54.8641 14.914 54.8721 15.042C54.8801 15.17 54.8841 15.288 54.8841 15.396V21H53.4621V12.324H54.6801C54.7521 12.324 54.8141 12.326 54.8661 12.33C54.9181 12.334 54.9641 12.344 55.0041 12.36C55.0481 12.376 55.0861 12.402 55.1181 12.438C55.1541 12.474 55.1881 12.522 55.2201 12.582L57.8181 17.4C57.8861 17.528 57.9481 17.66 58.0041 17.796C58.0641 17.932 58.1221 18.072 58.1781 18.216C58.2341 18.068 58.2921 17.926 58.3521 17.79C58.4121 17.65 58.4761 17.516 58.5441 17.388L61.1061 12.582C61.1381 12.522 61.1721 12.474 61.2081 12.438C61.2441 12.402 61.2821 12.376 61.3221 12.36C61.3661 12.344 61.4141 12.334 61.4661 12.33C61.5181 12.326 61.5801 12.324 61.6521 12.324H62.8701ZM67.6715 18.372C67.2435 18.392 66.8835 18.43 66.5915 18.486C66.2995 18.538 66.0655 18.606 65.8895 18.69C65.7135 18.774 65.5875 18.872 65.5115 18.984C65.4355 19.096 65.3975 19.218 65.3975 19.35C65.3975 19.61 65.4735 19.796 65.6255 19.908C65.7815 20.02 65.9835 20.076 66.2315 20.076C66.5355 20.076 66.7975 20.022 67.0175 19.914C67.2415 19.802 67.4595 19.634 67.6715 19.41V18.372ZM64.2755 15.702C64.9835 15.054 65.8355 14.73 66.8315 14.73C67.1915 14.73 67.5135 14.79 67.7975 14.91C68.0815 15.026 68.3215 15.19 68.5175 15.402C68.7135 15.61 68.8615 15.86 68.9615 16.152C69.0655 16.444 69.1175 16.764 69.1175 17.112V21H68.4455C68.3055 21 68.1975 20.98 68.1215 20.94C68.0455 20.896 67.9855 20.81 67.9415 20.682L67.8095 20.238C67.6535 20.378 67.5015 20.502 67.3535 20.61C67.2055 20.714 67.0515 20.802 66.8915 20.874C66.7315 20.946 66.5595 21 66.3755 21.036C66.1955 21.076 65.9955 21.096 65.7755 21.096C65.5155 21.096 65.2755 21.062 65.0555 20.994C64.8355 20.922 64.6455 20.816 64.4855 20.676C64.3255 20.536 64.2015 20.362 64.1135 20.154C64.0255 19.946 63.9815 19.704 63.9815 19.428C63.9815 19.272 64.0075 19.118 64.0595 18.966C64.1115 18.81 64.1955 18.662 64.3115 18.522C64.4315 18.382 64.5855 18.25 64.7735 18.126C64.9615 18.002 65.1915 17.894 65.4635 17.802C65.7395 17.71 66.0595 17.636 66.4235 17.58C66.7875 17.52 67.2035 17.484 67.6715 17.472V17.112C67.6715 16.7 67.5835 16.396 67.4075 16.2C67.2315 16 66.9775 15.9 66.6455 15.9C66.4055 15.9 66.2055 15.928 66.0455 15.984C65.8895 16.04 65.7515 16.104 65.6315 16.176C65.5115 16.244 65.4015 16.306 65.3015 16.362C65.2055 16.418 65.0975 16.446 64.9775 16.446C64.8735 16.446 64.7855 16.42 64.7135 16.368C64.6415 16.312 64.5835 16.248 64.5395 16.176L64.2755 15.702ZM74.3773 16.416C74.2093 16.212 74.0253 16.068 73.8253 15.984C73.6293 15.9 73.4173 15.858 73.1893 15.858C72.9653 15.858 72.7633 15.9 72.5833 15.984C72.4033 16.068 72.2493 16.196 72.1213 16.368C71.9933 16.536 71.8953 16.752 71.8273 17.016C71.7593 17.276 71.7253 17.584 71.7253 17.94C71.7253 18.3 71.7533 18.606 71.8093 18.858C71.8693 19.106 71.9533 19.31 72.0613 19.47C72.1693 19.626 72.3013 19.74 72.4573 19.812C72.6133 19.88 72.7873 19.914 72.9793 19.914C73.2873 19.914 73.5493 19.85 73.7653 19.722C73.9813 19.594 74.1853 19.412 74.3773 19.176V16.416ZM75.8593 12.084V21H74.9533C74.7573 21 74.6333 20.91 74.5813 20.73L74.4553 20.136C74.2073 20.42 73.9213 20.65 73.5973 20.826C73.2773 21.002 72.9033 21.09 72.4753 21.09C72.1393 21.09 71.8313 21.02 71.5513 20.88C71.2713 20.74 71.0293 20.538 70.8253 20.274C70.6253 20.006 70.4693 19.676 70.3573 19.284C70.2493 18.892 70.1953 18.444 70.1953 17.94C70.1953 17.484 70.2573 17.06 70.3813 16.668C70.5053 16.276 70.6833 15.936 70.9153 15.648C71.1473 15.36 71.4253 15.136 71.7493 14.976C72.0733 14.812 72.4373 14.73 72.8413 14.73C73.1853 14.73 73.4793 14.784 73.7233 14.892C73.9673 15 74.1853 15.146 74.3773 15.33V12.084H75.8593ZM81.2878 17.214C81.2878 17.022 81.2598 16.842 81.2038 16.674C81.1518 16.502 81.0718 16.352 80.9638 16.224C80.8558 16.096 80.7178 15.996 80.5498 15.924C80.3858 15.848 80.1938 15.81 79.9738 15.81C79.5458 15.81 79.2078 15.932 78.9598 16.176C78.7158 16.42 78.5598 16.766 78.4918 17.214H81.2878ZM78.4618 18.108C78.4858 18.424 78.5418 18.698 78.6298 18.93C78.7178 19.158 78.8338 19.348 78.9778 19.5C79.1218 19.648 79.2918 19.76 79.4878 19.836C79.6878 19.908 79.9078 19.944 80.1478 19.944C80.3878 19.944 80.5938 19.916 80.7658 19.86C80.9418 19.804 81.0938 19.742 81.2218 19.674C81.3538 19.606 81.4678 19.544 81.5638 19.488C81.6638 19.432 81.7598 19.404 81.8518 19.404C81.9758 19.404 82.0678 19.45 82.1278 19.542L82.5538 20.082C82.3898 20.274 82.2058 20.436 82.0018 20.568C81.7978 20.696 81.5838 20.8 81.3598 20.88C81.1398 20.956 80.9138 21.01 80.6818 21.042C80.4538 21.074 80.2318 21.09 80.0158 21.09C79.5878 21.09 79.1898 21.02 78.8218 20.88C78.4538 20.736 78.1338 20.526 77.8618 20.25C77.5898 19.97 77.3758 19.626 77.2198 19.218C77.0638 18.806 76.9858 18.33 76.9858 17.79C76.9858 17.37 77.0538 16.976 77.1898 16.608C77.3258 16.236 77.5198 15.914 77.7718 15.642C78.0278 15.366 78.3378 15.148 78.7018 14.988C79.0698 14.828 79.4838 14.748 79.9438 14.748C80.3318 14.748 80.6898 14.81 81.0178 14.934C81.3458 15.058 81.6278 15.24 81.8638 15.48C82.0998 15.716 82.2838 16.008 82.4158 16.356C82.5518 16.7 82.6198 17.094 82.6198 17.538C82.6198 17.762 82.5958 17.914 82.5478 17.994C82.4998 18.07 82.4078 18.108 82.2718 18.108H78.4618Z"
                                        fill="white"
                                    />
                                </svg>
                            ) : null}

                            <div className="meal-card" title={food.name}>
                                <div className="image-container food">
                                    {food.image ? (
                                        <ImgResized
                                            alt={food.name}
                                            src={food.image}
                                            width={500}
                                            height={500}
                                            className="recipe-image"
                                        />
                                    ) : (
                                        <div className="image-card-text-overlay">
                                            {food.brand_name ? (
                                                <span>
                                                    <h4>{typeMap[food.product_type] || food.product_type}</h4>
                                                    <h5>{food.brand_name}</h5>
                                                </span>
                                            ) : (
                                                <i className="icon-logo2">
                                                    <span className="path1" />
                                                    <span className="path2" />
                                                </i>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="meal-info">
                                    <footer>
                                        <div className="lj">
                                            {allMealKitBrands[food.brand_uuid] ? (
                                                <img src={allMealKitBrands[food.brand_uuid].dark_logo} height="24" />
                                            ) : null}
                                            <div className="dish-badge">Dish 1</div>
                                        </div>
                                        <FavoriteButton food={food} />
                                    </footer>
                                    <p>{food.pretty_name || food.name}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="triangle" style={{ left: isMobile ? 65 : 140 }} />
                </div>

                <section className={classNames('details-section', sections[section++])}>
                    {food.brand_name === 'Sunbasket' ? (
                        <Orders orders={[]}>
                            <div>
                                <p className="t3">
                                    <strong>This Sunbasket meal is not available in all areas. </strong>
                                    <br />
                                    Sunbasket currently ships to most zip codes in the United States, excluding AK, HI,
                                    MT, ND, and parts of NM. Not all delivery days are available in all areas. Once you
                                    enable Sunbasket meals via your Preference settings and enter your zip code, they
                                    will be automatically recommended to you based on timing, availability, and, most
                                    importantly, your nutrition prescription.
                                </p>
                            </div>
                        </Orders>
                    ) : null}

                    <header>
                        <div className="section-title">
                            <p className="t2">{dishTitle}</p>
                        </div>
                    </header>

                    <div className="section-body">
                        <MealTitle item={food} readOnly />

                        {this.renderPicsOrItDidntHappen()}

                        {food.ingredients_description ? (
                            <ol className="ingredients-list">
                                {food.ingredients_description.split('\n').map((line, i) => (
                                    <li key={i}>{line}</li>
                                ))}
                            </ol>
                        ) : null}

                        {food.ingredients && !food.ingredients_description ? (
                            <RecipeIngredients
                                recipe={food}
                                details={food}
                                subdetails={assets}
                                scaling={scaling}
                                profile={profile}
                            />
                        ) : null}

                        {food.description ? <p className="t3">{food.description}</p> : null}

                        {food.modifications ? (
                            <p className="t3 modifications">
                                <em>Recommended modifications:</em> {food.modifications}
                            </p>
                        ) : null}

                        {food.nutrition_disclaimer ? (
                            <Alert type="warning">
                                <strong>Nutrition Disclaimer:</strong> {food.nutrition_disclaimer}
                            </Alert>
                        ) : null}

                        {food.gtin_upc?.length ? (
                            <p className="t3">
                                <em>Barcode:</em> {food.gtin_upc.join(', ')}
                            </p>
                        ) : null}
                    </div>
                </section>

                {food.preparation ? (
                    <section className={classNames('details-section', sections[section++])}>
                        <header>
                            <div className="section-title">
                                <p className="t2">Dish Instructions</p>
                            </div>
                        </header>

                        <div className="section-body">
                            <RecipeInstructions recipe={food} details={food} user={profile} />
                        </div>
                    </section>
                ) : null}

                {!hide_nutrition || rd_override === true ? (
                    <section className={classNames('details-section', sections[section++])}>
                        <header>
                            <div className="section-title">
                                <p className="t2">
                                    Nutrition for <em>{food.pretty_name || food.name}</em>
                                </p>
                            </div>
                        </header>

                        <div className="section-body">
                            {areNutrientsEntered ? (
                                <>
                                    <div className="nutrition-portion">
                                        <p className="t2">Showing nutrition for:</p>
                                        <FoodUnitsSelector
                                            food={food}
                                            meal={portion}
                                            onChangeAmount={this.onChangePortionAmount}
                                            howMuchQuestion="Show nutrition for:"
                                        />
                                    </div>
                                    <NutritionInfo nutrients={nutrients} profile={profile} />
                                </>
                            ) : null}

                            {!areNutrientsEntered ? (
                                <>
                                    <p className="t3">Nutrition has not been entered for this food</p>
                                    {hasEditButton ? (
                                        <button className="el-medium-btn el-raspberry-btn" onClick={this.openEditModal}>
                                            Edit Nutrition
                                        </button>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    </section>
                ) : null}
            </>
        );
    };

    closeModal = () => {
        const { closeModal } = this.props;
        const { router } = this.context;

        if (closeModal) {
            return closeModal();
        }

        router.push('/');
    };

    render() {
        const { modalTitle } = this.props;
        const { food } = this.state;
        const { isMobile } = this.context;

        return (
            <>
                {food ? (
                    <Helmet
                        title={(food.pretty_name || food.name) + ' | EatLove'}
                        meta={[
                            { name: 'description', content: food.description },
                            { name: 'twitter:title', content: food.pretty_name || food.name },
                            { name: 'twitter:description', content: food.description },
                            { name: 'twitter:image', content: food.image },
                            { property: 'og:title', content: food.pretty_name || food.name },
                            { property: 'og:type', content: food.page_type },
                            {
                                property: 'og:url',
                                content: 'https://' + getConfig('self_host') + '/foods/' + food.uuid,
                            },
                            { property: 'og:image', content: food.image },
                            { property: 'og:description', content: food.description },
                        ]}
                    />
                ) : null}

                <Modal
                    isOpen={true}
                    onRequestClose={this.closeModal}
                    closeModal={this.closeModal}
                    className="el-modal el-modal1"
                    overlayClassName="el-modal-overlay"
                    contentLabel="Food Details"
                    closeTimeoutMS={250}
                >
                    <div className="el-modal-container el-modal1-container content-details-modal-container">
                        <header data-has-controls={isMobile}>
                            <button className="el-modal-back-btn" onClick={this.closeModal}>
                                <span>Back</span>
                            </button>

                            <h2>{modalTitle}</h2>

                            {isMobile ? (
                                <>
                                    {this.renderEditMealServingsButton()}

                                    <div className="el-modal-controls el-modal1-controls">
                                        <header>{this.renderControlBar()}</header>
                                    </div>
                                </>
                            ) : null}
                        </header>

                        <div className="el-modal-body-container el-modal1-body-container el-fonts">
                            {this.renderModalBody()}
                        </div>
                    </div>
                </Modal>
                {this.renderEditMealServingsModal()}
                {this.renderSchedulingModal()}
                {this.renderLogPortionsModal()}
                {this.renderFoodEditorModal()}
            </>
        );
    }
}
